import { useState, useEffect } from "react";
import { Modal } from "../../primitives/Modal";
import { Button } from "../../primitives/Button";
import { listFarmContacts, type FarmContact } from "../../../api/admin";

const DEFAULT_CC = "info@xlkey.ca";

type RecipientType = "to" | "cc" | "bcc";

type SendQuoteModalProps = {
  open: boolean;
  quoteNumber: string;
  clientId: string | null;
  primaryContactEmail: string | null;
  onClose: () => void;
  onSend: (recipients: { to: string[]; cc: string[]; bcc: string[] }) => void;
};

export function SendQuoteModal({
  open,
  quoteNumber,
  clientId,
  primaryContactEmail,
  onClose,
  onSend,
}: SendQuoteModalProps) {
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [addType, setAddType] = useState<RecipientType>("to");
  const [addEmail, setAddEmail] = useState("");
  const [contacts, setContacts] = useState<FarmContact[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      listFarmContacts(clientId)
        .then((list) => setContacts(list.filter((c) => c.person?.email)))
        .catch(() => setContacts([]));
    }
  }, [open, clientId]);

  useEffect(() => {
    if (!open) return;
    const toList = primaryContactEmail ? [primaryContactEmail] : [];
    setTo(toList);
    setCc([DEFAULT_CC]);
    setBcc([]);
    setAddEmail("");
  }, [open, primaryContactEmail]);

  const addRecipient = () => {
    const email = addEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (addType === "to" && !to.includes(email)) setTo((prev) => [...prev, email]);
    if (addType === "cc" && !cc.includes(email)) setCc((prev) => [...prev, email]);
    if (addType === "bcc" && !bcc.includes(email)) setBcc((prev) => [...prev, email]);
    setAddEmail("");
  };

  const removeFrom = (list: string[], setter: (fn: (prev: string[]) => string[]) => void, email: string) => {
    setter((prev) => prev.filter((e) => e !== email));
  };

  const addContactTo = (email: string, type: RecipientType) => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    if (type === "to" && !to.includes(e)) setTo((prev) => [...prev, e]);
    if (type === "cc" && !cc.includes(e)) setCc((prev) => [...prev, e]);
    if (type === "bcc" && !bcc.includes(e)) setBcc((prev) => [...prev, e]);
  };

  const handleSend = async () => {
    if (to.length === 0) return;
    setSending(true);
    try {
      await onSend({ to, cc, bcc });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Envoyer la soumission ${quoteNumber}`}
      showDefaultButtons={false}
      size="2xl"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text mb-1">Ajouter un courriel</label>
          <div className="flex gap-2 flex-wrap">
            <select
              value={addType}
              onChange={(e) => setAddType(e.target.value as RecipientType)}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
            >
              <option value="to">À</option>
              <option value="cc">Cc</option>
              <option value="bcc">Cci</option>
            </select>
            <input
              type="email"
              placeholder="ex: client@ferme.ca"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
              className="flex-1 min-w-[200px] h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
            />
            <Button label="Ajouter" intent="primary" size="sm" onClick={addRecipient} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-border bg-muted/5 p-3">
            <div className="text-xs font-semibold text-muted mb-2">À</div>
            {to.length === 0 ? (
              <p className="text-sm text-muted">Aucun courriel</p>
            ) : (
              <ul className="space-y-1">
                {to.map((email) => (
                  <li key={email} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-text">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeFrom(to, setTo, email)}
                      className="shrink-0 text-muted hover:text-danger"
                      aria-label={`Retirer ${email}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-border bg-muted/5 p-3">
            <div className="text-xs font-semibold text-muted mb-2">Cc</div>
            {cc.length === 0 ? (
              <p className="text-sm text-muted">Aucun courriel</p>
            ) : (
              <ul className="space-y-1">
                {cc.map((email) => (
                  <li key={email} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-text">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeFrom(cc, setCc, email)}
                      className="shrink-0 text-muted hover:text-danger"
                      aria-label={`Retirer ${email}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-border bg-muted/5 p-3">
            <div className="text-xs font-semibold text-muted mb-2">Cci</div>
            {bcc.length === 0 ? (
              <p className="text-sm text-muted">Aucun courriel</p>
            ) : (
              <ul className="space-y-1">
                {bcc.map((email) => (
                  <li key={email} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-text">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeFrom(bcc, setBcc, email)}
                      className="shrink-0 text-muted hover:text-danger"
                      aria-label={`Retirer ${email}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {contacts.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/5 p-3">
            <div className="text-xs font-semibold text-muted mb-2">Courriels disponibles</div>
            <ul className="space-y-2">
              {contacts.map((c) => {
                const email = c.person?.email?.trim();
                if (!email) return null;
                const label = [c.person.first_name, c.person.last_name].filter(Boolean).join(" ") || "Contact";
                const role = c.contact_role !== "other" ? c.contact_role : "";
                const sub = [role, c.is_primary ? "Principal" : ""].filter(Boolean).join(" • ");
                return (
                  <li key={c.id} className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm text-text">
                      {email}
                      {sub && <span className="text-muted text-xs ml-1">({sub})</span>}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        label="À"
                        intent="primary"
                        size="sm"
                        onClick={() => addContactTo(email, "to")}
                      />
                      <Button
                        label="Cc"
                        intent="default"
                        size="sm"
                        onClick={() => addContactTo(email, "cc")}
                      />
                      <Button
                        label="Cci"
                        intent="ghost"
                        size="sm"
                        onClick={() => addContactTo(email, "bcc")}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} />
          <Button
            label={sending ? "Envoi…" : "Envoyer"}
            intent="primary"
            size="sm"
            onClick={handleSend}
            state={sending ? "loading" : "default"}
            disabled={to.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
}
